import { createContext, useContext, useEffect, useMemo, useState, useCallback } from "react";
import { api } from "@/api/client";
import { useAuth } from "@/contexts/AuthContext";

/**
 * Iteration 28 — Per-user UI feature visibility.
 *
 * STAS can hide individual cards / tabs / buttons for any user. This
 * provider fetches the current user's effective hidden feature keys and
 * injects a `<style>` tag that hides any element carrying the matching
 * `data-feature` attribute.
 *
 * Components opt-in by either:
 *  • Wrapping in `<FeatureGate featureKey="dashboard.finance">…</FeatureGate>`
 *  • OR adding `data-feature="dashboard.finance"` on any element.
 *
 * STAS itself bypasses all hiding — Root must always see everything so
 * they can recover access.
 */
const Ctx = createContext({ hidden: new Set(), loading: true, refresh: async () => {} });

export function FeatureVisibilityProvider({ children }) {
  const { user } = useAuth();
  const [hidden, setHidden] = useState(new Set());
  const [loading, setLoading] = useState(true);

  const isRoot = !!user?.role?.is_root;

  const refresh = useCallback(async () => {
    if (!user) {
      setHidden(new Set());
      setLoading(false);
      return;
    }
    if (isRoot) {
      // STAS sees everything — never hide.
      setHidden(new Set());
      setLoading(false);
      return;
    }
    try {
      const { data } = await api.get("/auth/me/features");
      setHidden(new Set(data.hidden_features || []));
    } catch {
      setHidden(new Set());
    } finally {
      setLoading(false);
    }
  }, [user, isRoot]);

  useEffect(() => { refresh(); }, [refresh]);

  // Build the CSS rule list so any element with `data-feature="<key>"` is hidden
  const styleCss = useMemo(() => {
    if (!hidden.size) return "";
    return Array.from(hidden)
      .map((k) => `[data-feature="${k.replace(/"/g, '\\"')}"]`)
      .join(",\n") + " { display: none !important; }";
  }, [hidden]);

  const value = useMemo(
    () => ({ hidden, loading, refresh, isHidden: (k) => hidden.has(k) }),
    [hidden, loading, refresh]
  );

  return (
    <Ctx.Provider value={value}>
      {styleCss && <style data-testid="feature-visibility-style">{styleCss}</style>}
      {children}
    </Ctx.Provider>
  );
}

export const useFeatureVisibility = () => useContext(Ctx);

/** Render `children` only when the feature isn't hidden. Convenience wrapper. */
export function FeatureGate({ featureKey, children, fallback = null }) {
  const { isHidden } = useFeatureVisibility();
  if (isHidden(featureKey)) return fallback;
  return children;
}
